/**
 * CollegePicker — "which campus are you shopping?", asked at every sign-in.
 *
 * It used to be a one-time step and the campus was then fixed to the account
 * forever, which meant a student who tapped the wrong college on their first
 * day could not fix it — the only way out was a different Google account.
 * Now the answer is a per-session choice: sign out, sign back in, and this
 * asks again, the same email included.
 *
 * It blocks on `isCollegeConfirmed` rather than on the account having a
 * college, because a returning student's account already HAS one and must
 * still be asked. There is deliberately no dismiss: every catalogue query is
 * scoped to a campus, so a "later" button would only produce an empty store.
 */

import { useState } from 'react';
import { MapPin, Check } from 'lucide-react';
import { useCollege } from '@/context/CollegeContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useCart } from '@/context/CartContext';
import { Modal, Spinner } from '@/components/ui';

export function CollegePicker() {
  const { colleges, isLoadingColleges, isCollegeConfirmed, setCollege } = useCollege();
  const { status, customer, updateProfile } = useCustomerAuth();
  const { clear: clearCart } = useCart();
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  // Asked of every signed-in student who has not chosen yet this session.
  // Guests are already being sent to sign-in by RequireAuth.
  const mustChoose = status === 'authenticated' && !isCollegeConfirmed;
  if (!mustChoose) return null;

  const choose = async (id) => {
    setError('');
    setSaving(id);
    try {
      /**
       * The previous campus, read BEFORE saving. A cart is full of one
       * campus's products and the server scopes every product to the campus
       * the order names, so items carried across would fail checkout with
       * "one or more items are unavailable". Emptying is the honest outcome.
       */
      const previousCollegeId = String(customer?.college?._id ?? customer?.college ?? '');

      const account = await updateProfile({ college: id });

      if (previousCollegeId && previousCollegeId !== String(id)) clearCart();

      // Keep the browsing context in step with the account we just saved, and
      // record that the student chose it — this is what dismisses the picker.
      setCollege(account?.college?._id ?? id, { confirmed: true });
    } catch (err) {
      setError(err?.message ?? 'Could not save your campus. Please try again.');
      setSaving('');
    }
  };

  return (
    <Modal isOpen>
      <div className="mb-5 text-center">
        <img
          src="/brand/rocket07-mark.png"
          alt="Rocket07"
          width={56}
          height={56}
          className="mx-auto mb-3 h-14 w-14 object-contain"
        />
        <h2 className="text-lg font-extrabold text-ink">Choose your campus</h2>
        <p className="mt-1 text-sm text-muted">
          We&apos;ll show what&apos;s available at your college. Picked the wrong one? Sign out and
          sign in again to change it.
        </p>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{error}</div>
      )}

      {isLoadingColleges ? (
        <div className="grid place-items-center py-10">
          <Spinner className="text-primary-600" />
        </div>
      ) : colleges.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">
          No colleges are available yet. Please check back soon.
        </p>
      ) : (
        <div className="space-y-2">
          {colleges.map((c) => {
            // The campus this student used last. Now that the question is
            // asked at every sign-in, marking it turns the common case into a
            // single tap on something already recognisable.
            const isLast = String(c._id) === String(customer?.college?._id ?? customer?.college);
            return (
              <button
                key={c._id}
                type="button"
                disabled={Boolean(saving)}
                onClick={() => choose(c._id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                  isLast ? 'border-primary-500 bg-primary-50' : 'border-line hover:bg-canvas'
                }`}
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary-100 text-primary-600">
                  <MapPin size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{c.name}</span>
                  {c.city && <span className="text-xs text-muted">{c.city}</span>}
                </span>
                {saving === c._id ? (
                  <Spinner size="sm" className="text-primary-600" />
                ) : isLast ? (
                  <Check size={18} className="shrink-0 text-primary-600" />
                ) : (
                  <Check size={18} className="shrink-0 text-transparent" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

export default CollegePicker;
